import { useRef, useCallback } from "react";
import L from "../libs/leaflet";

import { generateId } from "../utils/generate";
import { polygonToGeoJSON } from "../geometry/polygon/polygonToGeoJSON";
import { createGeoJSONLayer } from "../layers/createGeoJSONLayer";
import { bindPopupToLeafletLayer } from "../popups/bindPopupToLeafletLayer";
import { normalizeGeoInput } from "../geometry/normalize";
import { normalizeLayerInputToFeatureCollection } from "../layers/normalizeLayerInputToFeatureCollection";

export default function useLayer(mapRef) {
  const layersRef = useRef(new Map()); // id → layer

  const getMap = useCallback(() => {
    return mapRef?.current?.map?.target ?? null;
  }, [mapRef]);

  const createPane = useCallback(
    (name, { zIndex = 400, pointerEvents = "none" } = {}) => {
      const map = getMap();
      if (!map) return null;

      let pane = map.getPane(name);
      if (!pane) {
        pane = map.createPane(name);
        pane.style.zIndex = zIndex;
        pane.style.pointerEvents = pointerEvents;
      }
      return pane;
    },
    [getMap],
  );

  const addLayer = useCallback(
    ({
      source,
      style,
      popupContent,
      pane,
      pointRenderer,
      visible = true,
      isUTM = true,
      autoOpenPopup = false,
    }) => {
      const map = getMap();
      if (!map || !source) return [];

      const deps = { polygonToGeoJSON, normalizeGeoInput };
      const sources = Array.isArray(source) ? source : [source];
      const results = [];

      sources.forEach((item) => {
        try {
          const layerId = item?.id || generateId();

          const geojson = normalizeLayerInputToFeatureCollection(
            item,
            layerId,
            isUTM,
            deps,
          );
          if (!geojson) return;

          const { layer } = createGeoJSONLayer(L, {
            geojson,
            layerId,
            item,
            style,
            pane,
            pointRenderer,
            popupContent,
            autoOpenPopup,
          });

          const shouldBeVisible =
            item?.visible !== undefined ? item.visible : visible;

          if (shouldBeVisible) {
            layer.addTo(map);
          }

          const bounds = layer.getBounds?.();
          layersRef.current.set(layerId, layer);

          results.push({
            id: layerId,
            layer,
            bounds: bounds?.isValid?.() ? bounds : null,
            openPopup: () => {
              const targetLayer = Object.values(layer?._layers ?? {})?.[0];

              targetLayer?.openPopup?.();
            },

            closePopup: () => {
              const targetLayer = Object.values(layer?._layers ?? {})?.[0];

              targetLayer?.closePopup?.();
            },

            getPopupLayer: () => {
              return Object.values(layer?._layers ?? {})?.[0] ?? null;
            },
          });
        } catch (err) {
          console.error("Error al agregar capa:", err);
        }
      });

      return results;
    },
    [getMap],
  );

  const updateLayer = useCallback(
    (
      id,
      {
        // updates
        style,
        popupContent,
        visible,
        pane,

        // advanced (recreate)
        source, // si viene, reemplaza geometría
        isUTM = true,
        pointRenderer, // si quieres permitir cambiarlo
        autoOpenPopup = false,

        // para popups tipo fn(feature,item,api)
        item,
      } = {},
    ) => {
      const map = getMap();
      const prevLayer = layersRef.current.get(id);
      if (!map || !prevLayer) {
        return { ok: false, recreated: false, layer: null };
      }

      const prevVisible = map.hasLayer(prevLayer);

      // Helpers
      const shouldTouchPopup = popupContent !== undefined;
      const shouldTouchStyle = style !== undefined;

      const wantsVisible =
        visible === undefined ? prevVisible : Boolean(visible);

      const wantsRecreate =
        // cambiar geometría => recrear
        source !== undefined ||
        // cambiar pane => recrear (lo más seguro)
        pane !== undefined ||
        // cambiar pointRenderer => recrear (porque pointToLayer sólo corre al construir)
        pointRenderer !== undefined;

      // =========================
      // 1) RECREATE PATH
      // =========================
      if (wantsRecreate) {
        try {
          // 1) obtener item base para normalizar
          // si source viene, úsalo; si no, intenta reconstruir desde la feature actual
          let baseItem = source;

          if (baseItem === undefined) {
            // intentamos tomar algo de la capa anterior
            // Para L.geoJSON -> layer.toGeoJSON()
            if (typeof prevLayer.toGeoJSON === "function") {
              baseItem = { geojson: prevLayer.toGeoJSON(), id };
            } else {
              // no se puede reconstruir
              return { ok: false, recreated: false, layer: prevLayer };
            }
          }

          const deps = { polygonToGeoJSON, normalizeGeoInput };
          const geojson = normalizeLayerInputToFeatureCollection(
            baseItem,
            id,
            isUTM,
            deps,
          );

          if (!geojson) {
            return { ok: false, recreated: false, layer: prevLayer };
          }

          // 2) crear nueva capa
          const { layer: nextLayer } = createGeoJSONLayer(L, {
            geojson,
            layerId: id,
            item: baseItem,
            style: shouldTouchStyle ? style : undefined,
            pane: pane, // si no viene, queda undefined y Leaflet usa default
            pointRenderer: pointRenderer, // idem
            popupContent: shouldTouchPopup ? popupContent : undefined,
            autoOpenPopup,
          });

          // 3) swap en el mapa (manteniendo visibilidad)
          if (prevVisible) map.removeLayer(prevLayer);

          layersRef.current.set(id, nextLayer);

          if (wantsVisible) {
            nextLayer.addTo(map);
          }

          return { ok: true, recreated: true, layer: nextLayer };
        } catch (e) {
          console.error("updateLayer(recreate) falló:", e);
          return { ok: false, recreated: false, layer: prevLayer };
        }
      }

      // =========================
      // 2) IN-PLACE UPDATE PATH
      // =========================

      // 2.1) style
      if (shouldTouchStyle && typeof prevLayer.setStyle === "function") {
        try {
          prevLayer.setStyle(style);
        } catch (e) {
          console.warn("updateLayer.setStyle falló:", e);
        }
      }

      // 2.2) popup
      if (shouldTouchPopup) {
        const shouldUnbind = popupContent === null || popupContent === false;

        const applyTo = (lyr) => {
          if (!lyr) return;

          if (shouldUnbind) {
            lyr.unbindPopup?.();
            return;
          }

          bindPopupToLeafletLayer({
            lyr,
            popup: popupContent,
            feature: lyr.feature,
            item,
          });
        };

        try {
          if (typeof prevLayer.eachLayer === "function") {
            prevLayer.eachLayer((lyr) => applyTo(lyr));
          } else {
            applyTo(prevLayer);
          }
        } catch (e) {
          console.warn("updateLayer.popup falló:", e);
        }
      }

      // 2.3) visible toggle
      if (visible !== undefined) {
        try {
          if (wantsVisible && !prevVisible) {
            prevLayer.addTo(map);
          } else if (!wantsVisible && prevVisible) {
            map.removeLayer(prevLayer);
          }
        } catch (e) {
          console.warn("updateLayer.visible falló:", e);
        }
      }

      return { ok: true, recreated: false, layer: prevLayer };
    },
    [getMap],
  );

  const getLayer = useCallback((id) => layersRef.current.get(id) || null, []);
  const listLayers = useCallback(
    () => Array.from(layersRef.current.keys()),
    [],
  );

  const removeLayer = useCallback(
    (id = "*") => {
      const map = getMap();
      if (!map) return;

      for (const [layerId, layer] of layersRef.current.entries()) {
        if (id === "*" || layerId === id) {
          map.removeLayer(layer);
          layersRef.current.delete(layerId);
        }
      }
    },
    [getMap],
  );

  const addSvgOverlay = useCallback(
    ({ svg, bounds, id = generateId(), zIndex = 400 }) => {
      const map = getMap();
      if (!map || !svg || !bounds) return null;

      let leafletBounds = bounds;

      if (Array.isArray(bounds) && bounds.length === 4) {
        leafletBounds = L.latLngBounds(
          [bounds[1], bounds[0]],
          [bounds[3], bounds[2]],
        );
      }

      if (!leafletBounds.getNorth) {
        console.warn("Bounds inválidos para svgOverlay", bounds);
        return null;
      }

      const container = document.createElement("div");
      container.innerHTML = svg.trim();

      const svgEl = container.querySelector("svg");
      if (!svgEl) {
        console.warn("SVG inválido");
        return null;
      }

      svgEl.style.pointerEvents = "none";

      const overlay = L.svgOverlay(svgEl, leafletBounds, {
        interactive: false,
        zIndex,
      });

      overlay.addTo(map);
      layersRef.current.set(id, overlay);

      return { id, layer: overlay };
    },
    [getMap],
  );

  // ✅ overlays gestionados por id -> cleanup()
  const overlaysRef = useRef(new Map()); // id -> cleanup fn | null

  const setOverlay = useCallback((id, create) => {
    // limpia el overlay anterior
    const prev = overlaysRef.current.get(id);
    if (typeof prev === "function") {
      try {
        prev();
      } catch (e) {
        console.warn("overlay cleanup falló:", e);
      }
    }

    const cleanup = typeof create === "function" ? create() : null;
    overlaysRef.current.set(id, typeof cleanup === "function" ? cleanup : null);
  }, []);

  const removeOverlay = useCallback((id) => {
    const prev = overlaysRef.current.get(id);
    if (typeof prev === "function") {
      try {
        prev();
      } catch (e) {
        console.warn("overlay cleanup falló:", e);
      }
    }
    overlaysRef.current.delete(id);
  }, []);

  const clearOverlays = useCallback(() => {
    for (const [, cleanup] of overlaysRef.current.entries()) {
      if (typeof cleanup === "function") {
        try {
          cleanup();
        } catch (e) {
          console.warn("overlay cleanup falló:", e);
        }
      }
    }
    overlaysRef.current.clear();
  }, []);

  return {
    addLayer,
    updateLayer,
    removeLayer,
    getLayer,
    listLayers,
    addSvgOverlay,
    createPane,
    setOverlay,
    removeOverlay,
    clearOverlays,
  };
}
